import { useEffect, useMemo, useState } from 'react'
import { Bell, BookOpen, CalendarDays, ChevronLeft, ChevronRight, ClipboardList, FileText, FolderOpen, GraduationCap, Heart, Lightbulb, Mail, MapPin, Monitor, Newspaper, Phone, ShieldCheck, Trophy, Users, Video, X, type LucideIcon } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { SectionTitle } from '../components/Common'
import { SchoolMap } from '../components/SchoolMap'
import { api } from '../services/api'
import type { AlbumItem, SchoolInfo } from '../types/content'
import heroBackground from '../../docs/ui/backgroud_slide.png'
import dailySchoolBackground from '../../docs/ui/background_moi-ngay-den-truong.png'
import admissionBackground from '../../docs/ui/background-san-sang-dong-hanh.png'
import schoolBuildingImage from '../../public/assets/images/school-building.jpg'
import heroSchoolImage from '../../public/assets/images/hero-school.jpg'
import activityExperienceImage from '../../public/assets/images/activity-experience.jpg'

// Fallback school info
const defaultSchool: SchoolInfo = {
  name: 'TRƯỜNG THCS LÝ THƯỜNG KIỆT',
  slogan: 'Đoàn kết - Tự tin - Sáng tạo - Phát triển',
  address: '123 Đường Lê Lợi, P. Tân Thạnh, TP. Tam Kỳ, Quảng Nam',
  phone: '0235 3821 234',
  email: 'c2ltk@tamky.edu.vn',
  website: 'https://thcslythuongkiet.tamky.edu.vn',
  workingHours: 'Thứ 2 - Thứ 6 (07:00 - 17:00)',
  mapLatitude: 15.5737,
  mapLongitude: 108.4742,
  mapZoom: 16,
}

const defaultQuickLinks = [
  { icon: ClipboardList, label: 'Lịch công tác', sub: 'Xem chi tiết', color: '#ffffff', bg: '#007c73', url: '/lich-cong-tac' },
  { icon: BookOpen, label: 'Văn bản', sub: 'Xem chi tiết', color: '#ffffff', bg: '#f24a36', url: '/van-ban' },
  { icon: Newspaper, label: 'Tin tức', sub: 'Xem chi tiết', color: '#ffffff', bg: '#f4b31b', url: '/tin-tuc' },
  { icon: FolderOpen, label: 'Thư viện', sub: 'Tham gia ngay', color: '#ffffff', bg: '#f24a36', url: '/thu-vien' },
]

const learningPaths = [
  { title: 'Chương trình chính khóa', text: 'Kiến thức nền tảng vững chắc, phát triển toàn diện.', image: '/assets/images/training-program.jpg' },
  { title: 'STEM & Công nghệ', text: 'Khơi gợi đam mê khám phá, sáng tạo không giới hạn.', image: '/assets/images/training-stem.jpg' },
  { title: 'Ngoại ngữ', text: 'Tự tin hội nhập, mở rộng cánh cửa thế giới.', image: '/assets/images/training-subjects.jpg' },
  { title: 'Kỹ năng sống', text: 'Rèn luyện kỹ năng, sống tích cực, trách nhiệm với cộng đồng.', image: '/assets/images/activity-football.jpg' },
]

const parentLinks = [
  'Hướng dẫn đăng ký tuyển sinh trực tuyến',
  'Cẩm nang phối hợp giáo dục',
  'Quy định đồng phục - Nề nếp học sinh',
  'Góc chia sẻ cùng phụ huynh',
  'Câu hỏi thường gặp',
]

const partnersFallback = [
  'Bộ Giáo dục và Đào tạo',
  'Sở Giáo dục và Đào tạo Quảng Nam',
  'Thành phố Tam Kỳ',
  'Trường THPT Nguyễn Du',
  'THCS & THPT Lê Thánh Tông',
]

// Map icon từ tên icon text (CMS) sang Lucide component
function iconComponent(iconName?: string) {
  const map: Record<string, LucideIcon> = {
    Bell, CalendarDays, GraduationCap, Monitor, Trophy, Mail, Users, BookOpen, FileText, Phone, MapPin,
  }
  return iconName ? (map[iconName] || CalendarDays) : CalendarDays
}

const fileURL = (url?: string) => {
  if (!url) return '#'
  if (/^https?:\/\//i.test(url)) return url
  return `${import.meta.env.VITE_CMS_URL || 'http://localhost:3001'}${url}`
}

const mediaURL = (url?: string) => {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('/assets/') || url.startsWith('/docs/') || url.startsWith('data:')) return url
  return `${import.meta.env.VITE_CMS_URL || 'http://localhost:3001'}${url}`
}

export function HomePage() {
  const { data, isLoading } = useQuery({ queryKey: ['home'], queryFn: api.getHome })
  const { data: programs = [] } = useQuery({ queryKey: ['programs'], queryFn: api.getPrograms })

  const school = data?.school ?? defaultSchool
  const schoolName = school.schoolName ?? school.name ?? defaultSchool.name
  const schoolAddress = school.address ?? defaultSchool.address
  const schoolPhone = school.phone ?? defaultSchool.phone
  const schoolEmail = school.email ?? defaultSchool.email
  const schoolWebsite = school.website ?? defaultSchool.website
  const workingHours = school.workingHours ?? defaultSchool.workingHours
  const mapLatitude = school.mapLatitude ?? defaultSchool.mapLatitude
  const mapLongitude = school.mapLongitude ?? defaultSchool.mapLongitude
  const mapZoom = school.mapZoom ?? defaultSchool.mapZoom

  const homeNews = data?.news ?? []
  const homeAnnouncements = data?.announcements ?? []
  const homeDocuments = data?.documents ?? []
  const homeAlbums = data?.albums ?? []
  const homeEvents = data?.events ?? []
  const slides = data?.homepage?.slides ?? []
  const statistics = data?.homepage?.statistics ?? []
  const quickLinks = defaultQuickLinks
  const partnerLinks = data?.homepage?.partnerLinks ?? []
  const journeyItems = programs.length > 0
    ? programs.slice(0, 4).map((program) => ({
      title: program.title,
      text: program.summary || 'Nội dung chương trình đào tạo đang được cập nhật.',
      image: mediaURL(program.image) || '/assets/images/training-program.jpg',
      url: `/dao-tao/${program.slug || program.id}`,
    }))
    : learningPaths.map((item) => ({ ...item, url: '/dao-tao' }))
  const [activeSlide, setActiveSlide] = useState(0)
  const [viewerAlbum, setViewerAlbum] = useState<AlbumItem | null>(null)
  const [viewerIndex, setViewerIndex] = useState(0)

  const heroSlides = useMemo(() => (
    slides.length > 0
      ? slides.map((slide) => ({ ...slide, photoUrl: mediaURL(slide.imageUrl) || schoolBuildingImage }))
      : [
        {
          title: 'NƠI NUÔI DƯỠNG\nTRI THỨC,\nCHẮP CÁNH TƯƠNG LAI',
          subtitle: 'Môi trường giáo dục thân thiện,\nkhơi dậy đam mê và hình thành\nnhững công dân có ích cho xã hội.',
          photoUrl: schoolBuildingImage,
        },
        {
          title: 'MỖI NGÀY ĐẾN TRƯỜNG\nLÀ MỘT NGÀY VUI',
          subtitle: 'Không gian học tập kỷ cương,\nsáng tạo và giàu yêu thương.',
          photoUrl: heroSchoolImage,
        },
        {
          title: 'CÙNG EM KHÁM PHÁ\nTRI THỨC MỚI',
          subtitle: 'Khơi mở năng lực cá nhân,\nbồi đắp tự tin và trách nhiệm.',
          photoUrl: activityExperienceImage,
        },
      ]
  ), [slides])
  const currentSlide = heroSlides[activeSlide] ?? heroSlides[0]
  const heroPhoto = currentSlide.photoUrl || schoolBuildingImage
  const heroTitle = currentSlide.title || 'NƠI NUÔI DƯỠNG\nTRI THỨC,\nCHẮP CÁNH TƯƠNG LAI'
  const heroSubtitle = currentSlide.subtitle || 'Môi trường giáo dục thân thiện,\nkhơi dậy đam mê và hình thành\nnhững công dân có ích cho xã hội.'
  const viewerItems = viewerAlbum
    ? (viewerAlbum.items?.length ? viewerAlbum.items : viewerAlbum.cover ? [viewerAlbum.cover] : [])
    : []
  const currentViewerItem = viewerItems[viewerIndex]
  const currentViewerURL = fileURL(currentViewerItem?.url)
  const currentIsVideo = currentViewerItem?.mimeType?.startsWith('video/') || viewerAlbum?.albumType === 'video'
  const canStepViewer = viewerItems.length > 1

  const openAlbum = (album: AlbumItem) => {
    setViewerAlbum(album)
    setViewerIndex(0)
  }

  const closeAlbum = () => {
    setViewerAlbum(null)
    setViewerIndex(0)
  }

  const stepViewer = (direction: -1 | 1) => {
    if (!viewerItems.length) return
    setViewerIndex((index) => (index + direction + viewerItems.length) % viewerItems.length)
  }

  useEffect(() => {
    setActiveSlide(0)
  }, [heroSlides.length])

  useEffect(() => {
    if (heroSlides.length <= 1) return undefined
    const timer = window.setInterval(() => {
      setActiveSlide((index) => (index + 1) % heroSlides.length)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [heroSlides.length])

  // Format giờ làm việc/thời gian từ startAt ISO string
  const formatTime = (isoString?: string) => {
    if (!isoString) return '07:30'
    try {
      const d = new Date(isoString)
      return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    } catch {
      return '07:30'
    }
  }

  // Lấy ngày hôm nay định dạng dd/mm/yyyy
  const getTodayString = () => {
    const today = new Date()
    const dd = String(today.getDate()).padStart(2, '0')
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const yyyy = today.getFullYear()
    return `Hôm nay: ${dd}/${mm}/${yyyy}`
  }

  return (
    <>
      <section className="home-hero">
        <div className="container home-hero__grid">
          <div className="home-hero__banner" style={{ backgroundImage: `url(${heroBackground})` }}>
            <img className="home-hero__photo" src={heroPhoto} alt={heroTitle.replace(/\n/g, ' ')} />
            <div className="home-hero__copy">
              <h1>{heroTitle.split('\n').map((line, i) => <span key={i}>{line}{i < heroTitle.split('\n').length - 1 && <br />}</span>)}</h1>
              <p>{heroSubtitle.split('\n').map((line, i) => <span key={i}>{line}{i < heroSubtitle.split('\n').length - 1 && <br />}</span>)}</p>
              <div className="home-hero__actions">
                <Link to="/gioi-thieu/ve-nha-truong">Khám phá nhà trường</Link>
                <Link to="/gioi-thieu">Tìm hiểu thêm</Link>
              </div>
            </div>
            {heroSlides.length > 1 && (
              <div className="hero-dots">
                {heroSlides.map((slide, i) => (
                  <button
                    type="button"
                    key={`${slide.photoUrl || 'slide'}-${i}`}
                    className={i === activeSlide ? 'active' : ''}
                    onClick={() => setActiveSlide(i)}
                    aria-label={`Chuyển đến ảnh ${i + 1}`}
                    aria-current={i === activeSlide ? 'true' : undefined}
                  />
                ))}
              </div>
            )}
          </div>
          <aside className="quick-panel">
            <h3>TRUY CẬP NHANH</h3>
            {quickLinks.map(({ icon: Icon, label, sub, color, bg, url }) => (
              <a href={url} key={label}>
                <span className="quick-panel__icon" style={{ background: bg, color: color }}>
                  <Icon size={20} />
                </span>
                <span className="quick-panel__text">
                  <strong>{label}</strong>
                  <small>{sub}</small>
                </span>
              </a>
            ))}
          </aside>
        </div>
      </section>

      <section className="container notice-ticker">
        <strong><Bell size={17} />Thông báo mới</strong>
        <Link to={homeAnnouncements[0]?.slug ? `/thong-bao/${homeAnnouncements[0].slug}` : '/thong-bao'}>
          <span className="notice-ticker__badge">Mới</span>
          {homeAnnouncements[0]?.title || 'Thông báo về việc nghỉ hè năm 2024'}
        </Link>
        <span>{homeAnnouncements[0]?.publishedAt ? new Date(homeAnnouncements[0].publishedAt).toLocaleDateString('vi-VN') : '06/05/2024'}</span>
        <Link to="/thong-bao">Xem tất cả <ChevronRight size={14} /></Link>
      </section>

      <div className="container home-layout">
        <div className="home-main">
          <section>
            <SectionTitle action={<Link to="/tin-tuc">Xem tất cả</Link>}>TIN TỨC & SỰ KIỆN</SectionTitle>
            {isLoading && <p>Đang tải tin tức...</p>}
            <div className="news-card-grid">
              {homeNews.slice(0, 3).map((item) => (
                <article className="news-card" key={item.id}>
                  <div className="news-card__image">
                    <img src={item.image || '/assets/images/news-opening.jpg'} alt={item.title} />
                    <span>{item.category}</span>
                  </div>
                  <div className="news-card__body">
                    <h3><Link to={`/tin-tuc/${item.slug}`}>{item.title}</Link></h3>
                    <small>{item.date ? new Date(item.date).toLocaleDateString('vi-VN') : ''}</small>
                    <p>{item.excerpt}</p>
                    <Link className="read-more" to={`/tin-tuc/${item.slug}`}>Xem chi tiết <ChevronRight size={14} /></Link>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className="schedule-card" id="lich-cong-tac">
          <SectionTitle>SỰ KIỆN SẮP DIỄN RA</SectionTitle>
          <div className="schedule-date">
            <span>‹</span>
            <strong>{getTodayString()}</strong>
            <span>›</span>
          </div>
          {homeEvents.length > 0 ? (
            homeEvents.slice(0, 3).map((item) => (
              <Link className="schedule-row" to={`/lich-cong-tac/${item.slug}`} key={item.id}>
                <strong>{formatTime(item.startAt)}</strong>
                <div>
                  {item.title}
                  <small>{item.location || 'Phòng họp'}</small>
                </div>
              </Link>
            ))
          ) : (
            <p style={{ fontSize: '11px', color: '#8a91a1', padding: '14px 0' }}>Không có lịch công tác nào hôm nay.</p>
          )}
          <Link className="schedule-card__button" to="/lich-cong-tac">Xem toàn bộ lịch công tác</Link>
        </aside>
      </div>

      <section className="daily-school">
        <div className="container daily-school__grid">
          <img src={dailySchoolBackground} alt="Mỗi ngày đến trường" />
          <div>
            <h2>Mỗi ngày đến trường<br />là một ngày vui</h2>
            <p>Chúng tôi cam kết xây dựng môi trường học tập kỷ cương, sáng tạo, yêu thương - nơi mỗi học sinh được tôn trọng, truyền cảm hứng và phát triển toàn diện.</p>
            <div className="value-row">
              <span><ShieldCheck /> <strong>Kỷ cương</strong><small>Nề nếp - trách nhiệm</small></span>
              <span><Lightbulb /> <strong>Sáng tạo</strong><small>Đổi mới - chủ động</small></span>
              <span><Heart /> <strong>Yêu thương</strong><small>Tôn trọng - sẻ chia</small></span>
            </div>
          </div>
        </div>
      </section>

      <section className="container triple-grid">
        <div className="panel-list">
          <SectionTitle action={<Link to="/thong-bao">Xem tất cả</Link>}>
            <span className="panel-title-icon"><Bell />THÔNG BÁO</span>
          </SectionTitle>
          {homeAnnouncements.slice(0, 5).map((item) => (
            <Link to={`/thong-bao/${item.slug || item.id}`} state={{ from: '/', fromLabel: 'Trang chủ' }} key={item.id}>
              <span>•</span>
              <strong>{item.title}</strong>
              <small>{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('vi-VN') : item.date}</small>
            </Link>
          ))}
        </div>

        <div className="panel-list document-list">
          <SectionTitle action={<Link to="/van-ban">Xem tất cả</Link>}>
            <span className="panel-title-icon"><FileText />VĂN BẢN MỚI</span>
          </SectionTitle>
          {homeDocuments.length > 0 ? (
            homeDocuments.slice(0, 5).map((item) => (
              <Link to="/van-ban" key={item.id}>
                <FileText size={18} />
                <strong>{item.title}</strong>
                <small>{item.issuedAt ? new Date(item.issuedAt).toLocaleDateString('vi-VN') : ''}</small>
              </Link>
            ))
          ) : (
            <p style={{ fontSize: '11px', color: '#8a91a1', padding: '10px 0' }}>Không có văn bản mới.</p>
          )}
        </div>

        <div className="panel-list parent-list">
          <SectionTitle action={<Link to="/lien-he">Xem tất cả</Link>}>
            <span className="panel-title-icon"><Users />DÀNH CHO PHỤ HUYNH</span>
          </SectionTitle>
          {parentLinks.map((item) => (
            <Link to="/thong-bao" key={item}>
              <Users size={17} />
              <strong>{item}</strong>
            </Link>
          ))}
        </div>
      </section>

      <section className="container learning-journey">
        <SectionTitle>HÀNH TRÌNH HỌC TẬP</SectionTitle>
        <div className="journey-grid">
          {journeyItems.map((item) => (
            <article key={item.title}>
              <Link className="journey-card__image" to={item.url} aria-label={item.title}>
                <img src={item.image} alt={item.title} />
              </Link>
              <div className="journey-card__body">
                <h3><Link to={item.url}>{item.title}</Link></h3>
                <p>{item.text}</p>
              </div>
              <Link className="journey-card__arrow" to={item.url} aria-label={item.title}><ChevronRight size={18} /></Link>
            </article>
          ))}
        </div>
      </section>

      <section className="container campus-moments">
        <SectionTitle action={<Link to="/thu-vien">Xem tất cả</Link>}>KHOẢNH KHẮC HỌC ĐƯỜNG</SectionTitle>
        <div className="moments-grid">
          {homeAlbums.slice(0, 7).map((album, index) => (
            <button type="button" className={index === 0 ? 'large' : ''} key={album.id} onClick={() => openAlbum(album)} aria-label={`Xem ${album.title}`}>
              <img src={album.cover?.url || '/assets/images/activity-art.jpg'} alt={album.title} />
              {album.albumType === 'video' && <Video />}
            </button>
          ))}
        </div>
      </section>

      {viewerAlbum && (
        <div className="library-viewer" role="dialog" aria-modal="true" aria-label={viewerAlbum.title}>
          <div className="library-viewer__panel">
            <div className="library-viewer__header">
              <div>
                <h2>{viewerAlbum.title}</h2>
                <p>{viewerAlbum.description || `${viewerIndex + 1}/${Math.max(viewerItems.length, 1)}`}</p>
              </div>
              <button type="button" onClick={closeAlbum} aria-label="Đóng"><X /></button>
            </div>

            <div className="library-viewer__stage">
              {canStepViewer && (
                <button type="button" className="library-viewer__nav library-viewer__nav--prev" onClick={() => stepViewer(-1)} aria-label="Trước"><ChevronLeft /></button>
              )}
              {currentViewerURL !== '#' && currentIsVideo ? (
                <video src={currentViewerURL} controls autoPlay />
              ) : currentViewerURL !== '#' ? (
                <img src={currentViewerURL} alt={currentViewerItem?.alt || viewerAlbum.title} />
              ) : (
                <p>Không tìm thấy media.</p>
              )}
              {canStepViewer && (
                <button type="button" className="library-viewer__nav library-viewer__nav--next" onClick={() => stepViewer(1)} aria-label="Tiếp"><ChevronRight /></button>
              )}
            </div>

            {viewerItems.length > 1 && (
              <div className="library-viewer__thumbs">
                {viewerItems.map((media, index) => (
                  <button type="button" className={index === viewerIndex ? 'active' : ''} key={`${media.url || 'media'}-${index}`} onClick={() => setViewerIndex(index)}>
                    {media.mimeType?.startsWith('video/') ? <Video /> : <img src={fileURL(media.url)} alt={media.alt || `${viewerAlbum.title} ${index + 1}`} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <section className="container stat-banner">
        {statistics.length > 0
          ? statistics.slice(0, 4).map((stat) => {
            const Icon = iconComponent(stat.icon)
            return (
              <div key={stat.label}>
                <Icon />
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            )
          })
          : (
            <>
              <div><BookOpen /><strong>18</strong><span>Lớp học</span></div>
              <div><Users /><strong>713</strong><span>Học sinh</span></div>
              <div><GraduationCap /><strong>41</strong><span>Cán bộ, giáo viên, nhân viên</span></div>
              <div><FileText /><strong>98.5%</strong><span>Tỷ lệ học sinh lên lớp</span></div>
            </>
          )
        }
      </section>

      <section
        className="container admission-cta"
        style={{ backgroundImage: `url(${admissionBackground})` }}
      >
        <div className="admission-cta__text">
          <h2>Sẵn sàng đồng hành<br />cùng nhà trường?</h2>
          <p>Tìm hiểu thông tin tuyển sinh và đăng ký trực tuyến nhanh chóng, thuận tiện.</p>
        </div>
        <div className="admission-cta__action">
          <Link to="/tuyen-sinh">Tìm hiểu tuyển sinh</Link>
          <span>Hoặc liên hệ: {schoolPhone}</span>
        </div>
      </section>

      {/* <section className="container partners">
        <SectionTitle>LIÊN KẾT - ĐỐI TÁC</SectionTitle>
        <div>
          {partnerLinks.length > 0
            ? partnerLinks.map((partner, index) => (
                <a href={partner.url || '#'} key={partner.name}>
                  <span>{index + 1}</span>{partner.name}
                </a>
              ))
            : partnersFallback.map((name, index) => (
                <a href="#" key={name}><span>{index + 1}</span>{name}</a>
              ))
          }
        </div>
      </section> */}

      <section className="container contact-strip">
        <div className="map-box">
          <SectionTitle>BẢN ĐỒ</SectionTitle>
          <SchoolMap
            title={schoolName}
            address={schoolAddress}
            latitude={mapLatitude}
            longitude={mapLongitude}
            zoom={mapZoom}
            className="school-map--home"
          />
        </div>
        <div className="contact-info">
          <SectionTitle>LIÊN HỆ NHÀ TRƯỜNG</SectionTitle>
          <p><MapPin />{schoolAddress}</p>
          <p><Phone />Điện thoại: {schoolPhone}</p><p><Mail />Email: {schoolEmail}</p><p><Monitor />Website: {schoolWebsite}</p><p><CalendarDays />Giờ làm việc: {workingHours}</p></div>
        <form className="mini-contact">
          <SectionTitle>GỬI LIÊN HỆ</SectionTitle>
          <input placeholder="Họ và tên *" />
          <input placeholder="Email *" />
          <input placeholder="Số điện thoại" />
          <textarea placeholder="Nội dung *" />
          <button type="button">Gửi liên hệ</button>
        </form>
      </section>
    </>
  )
}
